// noinspection JSUnusedGlobalSymbols

/*
 * Copyright (c) 2008-2025 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

/* global log */

import api from "@/config/api"
import {
  MEETING_LOADED,
  MEETINGS_LOADED,
  MEETING_CREATED,
  MEETINGS_CREATED,
  MEETING_PERMISSIONS_LOADED,
} from "@/config/events"
import { v4 as UUIDv4 } from "uuid"
import Vue from "vue"
import communication from "~/common/src/messaging" // or simply 'messaging' (but this is not detected by IDEA)
import { LOAD_STATES, PagedRequestHandler } from "../utils/PagedRequestHandler"
import { defaultUserProperties } from "@/config/userPropertyConstants"
import { EventBus } from "~/common/src/event-bus"
import { PERMISSIONS } from "@/config/permissions"
import store from "./index"

EventBus.$on([MEETING_PERMISSIONS_LOADED], () => {
  store.dispatch("meetings/loadCollaborators")
})

const meetingRequestHandler = Vue.observable(new PagedRequestHandler(api.MEETINGS))

const requestFilter = function (filterCriteria, sortOption, rootGetters) {
  return filterCriteria.reduce((finalObject, elem) => {
    let selectedOptions = elem.options.reduce((finalString, option) => {
      // filters shouldn't be added to the request if the required permission is missing
      if (option.requiredPermission && !store.getters["auth/hasPermission"](option.requiredPermission))
        return finalString

      if (option.selected) finalString += option.urlValue + ","
      return finalString
    }, "")

    if (selectedOptions.length) finalObject[elem.type] = selectedOptions.substring(0, selectedOptions.length - 1)
    finalObject["sort"] = sortOption.urlValue + (sortOption.asc ? ",asc" : ",desc")

    if (rootGetters["tenants/hasTenantFilter"]) {
      finalObject["tenantId"] = rootGetters["tenants/getTenantFilterId"]
    }

    return finalObject
  }, {})
}

function performMergeStrategy(meeting, properties) {
  for (let property in properties) {
    if (meeting.hasOwnProperty(property)) {
      Vue.set(meeting, property, properties[property])
      Vue.delete(properties, property)
    }
  }
}

function storeBreakoutMeeting(state, meeting) {
  let parentId = meeting.parentId
  if (!parentId) {
    return
  }

  if (meeting.type === "WAITING") {
    return
  }

  let parentMeeting = state.meetings[parentId]
  log.debug("Storing breakout meeting", meeting.id, "under its parent:", parentId)

  // Create parent meeting if it doesn't exist yet
  if (!parentMeeting) {
    parentMeeting = {}
    parentMeeting.id = parentId
    Vue.set(state.meetings, parentId, parentMeeting)
  }

  if (!Array.isArray(parentMeeting.breakoutMeetings)) {
    // Create array for breakout meetings lazily
    Vue.set(parentMeeting, "breakoutMeetings", [])
  }

  // check if breakout meeting already exist - if so replace it otherwise append it
  let breakoutIndex = parentMeeting.breakoutMeetings.findIndex((b) => b.id === meeting.id)
  if (breakoutIndex >= 0) {
    parentMeeting.breakoutMeetings[breakoutIndex] = meeting
  } else {
    parentMeeting.breakoutMeetings.push(meeting)
  }

  // Store breakout meetingId to mapping for later lookup
  Vue.set(state.breakoutMainMapping, meeting.id, parentId)
}

function getMainMeetingId(state, id) {
  if (state.meetings.hasOwnProperty(id)) {
    // Main meeting with the given ID exists
    return id
  }
  // Lookup main meeting ID from given breakout meeting ID
  return state.breakoutMainMapping[id]
}

function getMeetingById(state, id) {
  // Check if it's a main meeting
  let meeting = state.meetings[id]
  if (meeting) {
    return meeting
  }

  // We are looking for a breakout meeting - lookup ID of it's parentId and do a linear search of the breakout meeting
  // As the number of breakout meetings per main meeting is usually pretty small a linear search doesn't hurt too much
  let mainMeetingId = state.breakoutMainMapping[id]
  if (mainMeetingId) {
    let meeting = state.meetings[mainMeetingId]
    if (meeting && meeting.breakoutMeetings) {
      return meeting.breakoutMeetings.find((b) => b.id === id)
    } // else: meeting doesn't exist
  } //  else: breakout ID not contained in breakoutMainMapping
}

/**
 * Stores the given meeting - if it's a main meeting including any breakout meetings in state.meetings object. In case
 * it's a breakout meeting it's stored in its mainMeeting's breakoutMeetings array.
 *
 * Does not add main meetings to state.meetingKeys (required for appearing in meeting list).
 *
 * @param state
 * @param m The main or breakout meeting to store
 */
function storeMeeting(state, m) {
  let meetingId = m.id
  if (state.meetingProperties[meetingId]) {
    log.debug("Meeting properties exist; performing simple merge strategy")
    performMergeStrategy(m, state.meetingProperties[meetingId])
  }

  if (!m.parentId) {
    log.debug("Storing main meeting on top level", meetingId)
    Vue.set(state.meetings, meetingId, m)
    if (m.breakoutMeetings) {
      m.breakoutMeetings.forEach((b) => Vue.set(state.breakoutMainMapping, b.id, meetingId))
    }
  } else {
    storeBreakoutMeeting(state, m)
  }
}

export default {
  namespaced: true,

  state: {
    dirtyMeetingList: false,
    meetings: {}, // mainMeetings only; breakouts are stored as part of their parents in breakoutMeetings
    breakoutMainMapping: {}, // Mapping from breakout meeting ID to main meeting ID
    waitingRoomIds: {}, // Mapping from main meeting ID to waiting room ID
    waitingRooms: {}, // Mapping from waiting room IDs to waiting room data
    meetingKeys: [], // IDs of main meetings only (for appearance in meeting list)
    meetingCounts: {}, // tenantId to count publication data (totalCount -> Number, totalOpenCount -> Number, openCounts -> real meeting type -> Number)
    interactions: {},
    meetingInteractions: {},
    autojoinId: null,
    autojoinFilter: null,
    meetingProperties: {},
    meetingCollaborators: {},
    authSettings: {},
    browsingEnabled: {},
    status: null,
    sortAndFilterInitPromise: null,
    statistics: {
      participants: 56,
      participation: "93%",
      interactions: 3,
      interactionParticipation: "80%",
      feedback: 41,
      feedbackParticipation: "73%",
    },
    // Mapping from meetingId to StreamingServer data object
    streamingServers: {},
    searchQuery: "",
    initialized: false,
    meetingFilterCriteria: [
      {
        type: "timeFilters",
        options: [
          {
            text: "pastMeetings",
            urlValue: "past",
            selected: true,
            default: true,
          },
          {
            text: "futureMeetings",
            urlValue: "future",
            selected: true,
            default: true,
          },
        ],
      },
      {
        type: "openFilters",
        options: [
          {
            text: "openMeetings",
            urlValue: "open",
            selected: true,
            default: true,
          },
          {
            text: "closedMeetings",
            urlValue: "closed",
            selected: true,
            default: true,
          },
        ],
      },
      {
        type: "ownerFilters",
        options: [
          {
            text: "ownMeetings",
            urlValue: "own",
            selected: true,
            default: true,
          },
          {
            text: "sharedMeetings",
            urlValue: "shared",
            selected: true,
            default: true,
          },
          {
            text: "foreignMeetings",
            urlValue: "foreign",
            selected: false,
            default: false,
            adminDefault: true,
            requiredPermission: PERMISSIONS.VIEW_MEETING,
          },
        ],
      },
      {
        type: "lifecycleFilters",
        options: [
          {
            text: "existingMeetings",
            urlValue: "existing",
            selected: true,
            default: true,
            requiredPermission: PERMISSIONS.VIEW_MEETING_HIDDEN,
            hideWithTenantFilter: true,
          },
          {
            text: "hiddenMeetings",
            urlValue: "hidden",
            selected: false,
            default: false,
            requiredPermission: PERMISSIONS.VIEW_MEETING_HIDDEN,
            hideWithTenantFilter: true,
          },
        ],
      },
      {
        type: "autojoinFilter",
        options: [
          {
            text: "autojoinMeeting",
            urlValue: null,
            selected: false,
            default: false,
            requiredPermission: PERMISSIONS.VIEW_AUTOJOIN_MEETING,
            hideWithTenantFilter: true,
          },
        ],
      },
    ],
    sortOptions: {
      active: 0,
      options: [
        {
          text: "ascendingTitle",
          urlValue: "title",
          asc: true,
        },
        {
          text: "descendingTitle",
          urlValue: "title",
          asc: false,
        },
        {
          text: "ascendingEventDate",
          urlValue: "startDate",
          asc: true,
        },
        {
          text: "descendingEventDate",
          urlValue: "startDate",
          asc: false,
        },
        {
          text: "ascendingCreationDate",
          urlValue: "creationTimestamp",
          asc: true,
        },
        {
          text: "descendingCreationDate",
          urlValue: "creationTimestamp",
          asc: false,
        },
      ],
    },
  },

  mutations: {
    /**
     * commit load initiation to state
     */
    loadStart(state) {
      state.status = "loading"
    },

    /**
     * commit sortAndFilterInitPromise to state
     */
    sortAndFilterInitPromise(state, sortAndFilterInitPromise) {
      state.sortAndFilterInitPromise = sortAndFilterInitPromise
    },

    /**
     * commit meetings to state
     * (with adding them to the sorted meetingKeys list for display in meeting list)
     *
     * @param state
     * @param meetings
     * @param purge - flag to force a purge of old meeting data
     */
    loadMeetings(state, { meetings, purge = false }) {
      if (purge) {
        // if purge is requested,
        // delete all previous meetings
        state.meetingKeys = []
        state.meetings = {}
      }

      // store meeting objects
      meetings.forEach((m) => {
        storeMeeting(state, m)

        // Store meeting IDs of main meetings
        if (!m.parentId) {
          state.meetingKeys.push(m["id"])
        }
      })

      // update status
      state.status = "success"
      state.dirtyMeetingList = false

      EventBus.$emit(MEETINGS_LOADED)
    },

    /**
     * commit one meeting to state
     * (without adding it to the sorted meetingKeys list for display in meeting list)
     *
     * @param state
     * @param meeting
     */
    loadMeeting(state, { meeting }) {
      storeMeeting(state, meeting)

      // Only add to meetingKeys if not present
      if (state.meetingKeys.indexOf(meeting["id"]) < 0 && !meeting.parentId) {
        state.meetingKeys.push(meeting["id"])
      }

      EventBus.$emit(MEETING_LOADED)
    },

    /**
     * commit load error to state
     */
    loadError(state) {
      state.status = "error"
    },

    /**
     * Updates the publication data of the meetingCounts subscription.
     *
     * @param state
     * @param tenantId
     * @param data
     */
    setMeetingCounts(state, { tenantId, data }) {
      Vue.set(state.meetingCounts, tenantId, data)
    },

    /**
     * commit new autojoin id to state
     *
     * @param state
     * @param id
     */
    setAutojoin(state, id) {
      Vue.set(state, "autojoinId", id)
    },

    /**
     * commit new autojoin parent meeting id to state
     *
     * @param state
     * @param id
     */
    setAutojoinFilter(state, id) {
      Vue.set(state, "autojoinFilter", id)
    },

    /**
     * Init filters in state. Commit given filter config (selectedFilters) to state by enabling all filter options
     * specified and disabling all others.
     *
     * Note: Do not call this mutation directly. Only to be used by initSortAndFilter action.
     *
     * @param state
     * @param selectedFilters in format { "timeFilters": ["futureMeetings", "pastMeetings"], "xyFilters": ... }
     */
    setMeetingFilterCriteria(state, selectedFilters) {
      console.log("----- mutation: setMeetingFilterCriteria")
      // iterate over available filters (filter groups)
      state.meetingFilterCriteria.forEach((filter) => {
        // iterate over available options per filter group and enable those contained in `selectedFilters`
        let selectedOptionsCount = 0
        filter.options.forEach((option) => {
          option.selected = selectedFilters?.[filter.type]?.includes(option.text)
          if (option.selected) selectedOptionsCount++
        })

        // If autojoinFilter isn't set and all values from one filter type are unselected, select default ones
        if (selectedOptionsCount === 0 && selectedFilters.autojoinFilters?.length === 0) {
          filter.options.forEach((element) => {
            element.selected = element.default
          })
        }
      })
    },

    /**
     * Update meeting filter criteria (toggle given filter option and commit to state).
     *
     * Note: Do not call this mutation directly. Use action with same name instead
     * (as it also updates meetingRequestHandler and triggers reload of meetings).
     *
     * @param state
     * @param filterIdx The filter group to update
     * @param optionIdx The option to toggle
     */
    updateMeetingFilterCriteria(state, { filterIdx, optionIdx }) {
      state.meetingFilterCriteria[filterIdx].options[optionIdx].selected =
        !state.meetingFilterCriteria[filterIdx].options[optionIdx].selected

      state.meetingFilterCriteria.forEach((filter) => {
        // If autojoin filter was enabled: disable every other filter
        // If autojoin filter was disabled: enable every other filter
        if (state.meetingFilterCriteria[filterIdx].type === "autojoinFilter") {
          if (filter.type === "autojoinFilter") return

          filter.options.forEach((option) => {
            option.selected = !state.meetingFilterCriteria[filterIdx].options[optionIdx].selected
          })
        }
        // If another filter was enabled while autojoin was set, disable autojoin filter and enable every other
        else if (this.getters["meetings/isAutojoinFilterActive"]) {
          filter.options.forEach((option) => {
            option.selected = filter.type !== "autojoinFilter" // option.default
          })
        }
      })

      // If all values from one filter type are unselected, select default ones
      let stateAllowed = false
      state.meetingFilterCriteria[filterIdx].options.forEach((element) => {
        if (element.selected) stateAllowed = true
      })

      if (!stateAllowed) {
        state.meetingFilterCriteria[filterIdx].options.forEach((element) => {
          element.selected = element.default
        })
      }
    },

    /**
     * Reset meeting filter criteria to default values and commit to state.
     *
     * Note: Do not call this mutation directly. Use action with same name instead
     * (as it also updates meetingRequestHandler and triggers reload of meetings).
     *
     * @param state
     */
    resetMeetingFilterCriteria(state) {
      console.log("----- mutation: resetMeetingFilterCriteria")
      const selectedTenantId = this.getters["tenants/getTenantFilterId"]
      state.meetingFilterCriteria.forEach((filter) =>
        filter.options.forEach((option) => {
          if (!selectedTenantId && option.hasOwnProperty("adminDefault")) {
            option.selected = option.adminDefault
          } else {
            option.selected = option.default
          }
        })
      )
    },

    /**
     * Commit specified sort option.
     *
     * Note: Do not call this mutation directly. Use action with same name instead
     * (as it also updates meetingRequestHandler and triggers reload of meetings).
     *
     * Exception: initSortAndFilter action calls this directly and does handler update on its own.
     *
     * @param state
     * @param sortVal the idx of the sort option to use
     */
    setLastActiveSort(state, sortVal) {
      Vue.set(state.sortOptions, "active", sortVal)
    },

    /**
     * commit new search query value
     *
     * Note: Do not call this mutation directly. Use action with same name instead
     * (as it also updates meetingRequestHandler and triggers reload of meetings).
     *
     * @param state
     * @param search
     */
    setSearchQuery(state, search) {
      state.searchQuery = search
    },

    /**
     * commit new meeting to state
     *
     * @param state
     * @param data
     * @param id
     */
    createMeeting(state, { data, id }) {
      state.dirtyMeetingList = true

      storeMeeting(state, data)

      // We set the meetingKeys here although the next time the MeetingList is loaded the data is purged anyway
      if (!data.parentId) {
        state.meetingKeys.push(id)
      }

      EventBus.$emit(MEETING_CREATED)
    },

    /**
     * commit new meetings to state (called from uploadMeeting action)
     *
     * @param state
     * @param data
     */
    createMeetings(state, { data }) {
      state.dirtyMeetingList = true

      if (!Array.isArray(data)) {
        log.error("Weird didn't receive an array")
        return
      }

      data.forEach((m) => {
        storeMeeting(state, m)

        // We set the meetingKeys here although the next time the MeetingList is loaded the data is purged anyway
        if (!m.parentId) {
          state.meetingKeys.push(m.id)
        }
      })
      EventBus.$emit(MEETINGS_CREATED)
    },

    /**
     * commit meeting update to state
     *
     * @param state
     * @param data
     */
    updateMeeting(state, { data }) {
      state.dirtyMeetingList = true

      storeMeeting(state, data)
    },

    /**
     * commit browsingEnabled to state
     *
     * @param state
     * @param meetingId
     * @param browsingEnabled
     */
    setBrowsingEnabled(state, { meetingId, browsingEnabled }) {
      Vue.set(state.browsingEnabled, meetingId, browsingEnabled)
    },

    /**
     * mark meeting list as dirty so it will be reloaded upon next display
     *
     * @param state
     */
    setMeetingListDirty(state) {
      state.dirtyMeetingList = true
    },

    /**
     * commit meeting hide action to state
     *
     * @param data
     * @param state
     * @param id
     */
    hideMeeting(state, { id }) {
      state.dirtyMeetingList = true

      let meeting = getMeetingById(state, id)
      if (!meeting) {
        return
      }

      if (!meeting.parentId) {
        // Delete main meeting from meeting list
        let index = state.meetingKeys.indexOf(meeting.id)
        if (index >= 0) {
          state.meetingKeys.splice(index, 1)
        }

        // Delete mappings from its breakout meetings
        meeting.breakoutMeetings?.forEach((b) => delete state.breakoutMainMapping[b.id])

        // Delete meeting (this also deletes the breakout meetings)
        delete state.meetings[meeting.id]

        // Delete waiting room
        let waitingRoomId = state.waitingRoomIds[meeting.id]
        delete state.waitingRooms[waitingRoomId]
        delete state.waitingRoomIds[meeting.id]
      } else {
        // Deleting breakout meeting
        let mainMeetingId = state.breakoutMainMapping[id]
        let mainMeeting = state.meetings[mainMeetingId]
        delete state.breakoutMainMapping[id]

        // Deleting breakout meeting - remove from parent's breakoutMeetings list
        let breakoutIndex = mainMeeting.breakoutMeetings.findIndex((b) => b.id === meeting.id)
        if (breakoutIndex >= 0) {
          mainMeeting.breakoutMeetings.splice(breakoutIndex, 1)
        } // else: Breakout meeting doesn't exist
      }

      // Delete meeting properties
      delete state.meetingProperties[id]

      // Delete meeting's interactions if they've been loaded
      let meetingInteractions = state.meetingInteractions[id]
      if (Array.isArray(meetingInteractions)) {
        meetingInteractions.forEach((interactionId) => delete state.interactions[interactionId])
        delete state.meetingInteractions[id]
      }
    },

    /**
     * Starts the given meeting by setting the open property to true for the main meeting as well as its breakout
     * meetings. The autoClose parameters describe how the meeting is stopped automatically if not stopped earlier
     * manually.
     *
     * Note: the given id is resolved to the corresponding mainMeeting ID
     *
     * @param state
     * @param id
     * @param autoCloseAfterLastLifeSignMinutes
     * @param autoCloseAtTime
     */
    startMeeting(state, { id, autoCloseAfterLastLifeSignMinutes, autoCloseAtTime }) {
      // Determine main meeting
      let mainMeetingId = getMainMeetingId(state, id)
      let mainMeeting = getMeetingById(state, mainMeetingId)

      Vue.set(mainMeeting, "autoCloseAfterLastLifeSignMinutes", autoCloseAfterLastLifeSignMinutes)
      Vue.set(mainMeeting, "autoCloseAtTime", autoCloseAtTime)

      // Open meeting(s)
      mainMeeting.breakoutMeetings?.forEach((b) => {
        Vue.set(b, "open", true)
      })

      Vue.set(mainMeeting, "open", true)

      log.info("Meeting started:", mainMeeting.id)
    },

    /**
     * Stops the given meeting by setting the open property to false for the main meeting as well as its breakout
     * meetings.
     *
     * Note: the given id is resolved to the corresponding mainMeeting ID
     *
     * @param state
     * @param id
     */
    closeMeeting(state, { id }) {
      // Determine main meeting
      let mainMeetingId = getMainMeetingId(state, id)
      let mainMeeting = getMeetingById(state, mainMeetingId)

      // Close meeting(s)
      mainMeeting.breakoutMeetings?.forEach((b) => {
        Vue.set(b, "open", false)
      })

      Vue.set(mainMeeting, "open", false)

      log.info("Meeting closed:", mainMeeting.id)
    },

    /**
     * Adds the waiting room info to the state.
     *
     * @param state
     * @param mainMeetingId
     * @param waitingRoom
     */
    setWaitingRoom(state, { mainMeetingId, waitingRoom }) {
      Vue.set(state.waitingRoomIds, mainMeetingId, waitingRoom.id)
      Vue.set(state.waitingRooms, waitingRoom.id, waitingRoom)
    },

    /**
     * Opens the waiting room of the given meetingID by setting its property open to true.
     *
     * Note: the given id is resolved to the corresponding mainMeeting ID
     *
     * @param state
     * @param id
     */
    startWaitingRoom(state, { id }) {
      // Determine main meeting
      let mainMeetingId = getMainMeetingId(state, id)

      let waitingRoomId = state.waitingRoomIds[mainMeetingId]
      if (waitingRoomId) {
        let waitingRoom = state.waitingRooms[waitingRoomId]
        Vue.set(waitingRoom, "open", true)
      } else {
        log.warn("Waiting room info not available")
      }
    },

    /**
     * Closes the waiting room of the given meetingID by setting its property open to false.
     *
     * Note: the given id is resolved to the corresponding mainMeeting ID
     *
     * @param state
     * @param id
     */
    closeWaitingRoom(state, { id }) {
      // Determine main meeting
      let mainMeetingId = getMainMeetingId(state, id)

      let waitingRoomId = state.waitingRoomIds[mainMeetingId]
      if (waitingRoomId) {
        let waitingRoom = state.waitingRooms[waitingRoomId]
        Vue.set(waitingRoom, "open", false)
      } else {
        log.warn("Waiting room info not available")
      }
    },

    disableWaitingRoomSupport(state, { id }) {
      // Determine main meeting
      let mainMeetingId = getMainMeetingId(state, id)
      let mainMeeting = getMeetingById(state, mainMeetingId)

      if (mainMeeting) {
        Vue.set(mainMeeting, "waitingRoomSupport", false)
      } else {
        log.warn("Main meeting not available")
      }
    },

    setAutoCloseInMinutes(state, { id, autoCloseInMinutes }) {
      // Determine main meeting
      let mainMeetingId = getMainMeetingId(state, id)
      let mainMeeting = getMeetingById(state, mainMeetingId)

      if (mainMeeting) {
        Vue.set(mainMeeting, "autoCloseInMinutes", autoCloseInMinutes)
      } else {
        log.warn("Main meeting not available")
      }
    },

    unsetAutoCloseInMinutes(state, { id }) {
      // Determine main meeting
      let mainMeetingId = getMainMeetingId(state, id)
      let mainMeeting = getMeetingById(state, mainMeetingId)

      if (mainMeeting) {
        Vue.delete(mainMeeting, "autoCloseInMinutes")
      } else {
        log.warn("Main meeting not available")
      }
    },

    updateProperty(state, { id, key, value }) {
      let meeting = getMeetingById(state, id)
      if (meeting) {
        state.dirtyMeetingList = true
        Vue.set(meeting, key, value)
      }
    },

    setMeetingProperties(state, { id, properties }) {
      log.debug("Received meeting properties for:", id, ":", properties)
      let meeting = getMeetingById(state, id)
      if (meeting) {
        log.debug("Performing simple merge strategy")

        performMergeStrategy(meeting, properties)

        log.debug("Adding remaining custom properties: " + JSON.stringify(properties))
      }

      Vue.set(state.meetingProperties, id, properties)
    },

    setMeetingProperty(state, { id, key, value }) {
      log.debug("Setting meeting property: ", id, "-", key, "=", value)

      let props = state.meetingProperties[id]
      if (!props) {
        Vue.set(state.meetingProperties, id, {})
      }
      Vue.set(state.meetingProperties[id], key, value)
    },

    // pending: Maybe split up in multiple store modules?

    updateAuthSettings(state, { id, data }) {
      log.debug("Setting authSetting of meeting", id, ":", data)

      Vue.set(state.authSettings, id, data)
    },

    // --------------------------------- Interaction mutations ---------------------------------

    insertInteraction(state, { meetingId, index, interaction }) {
      let foundInteraction = state.interactions[interaction.interactionId]
      if (foundInteraction) {
        if (typeof foundInteraction.isPlaceholder != "undefined" && foundInteraction.isPlaceholder === true) {
          // placeholder in script - probably a cloned interaction coming back from server
          log.debug("Replacing placeholder with ID ", interaction.interactionId, " by eventual interaction.")
          Vue.set(state.interactions, interaction.interactionId, interaction)
          return
        } else {
          // interaction already in script - probably insert coming back from server
          log.trace("Interaction already contained in list - ignoring")
          return
        }
      }

      log.debug(
        "Inserting interaction of type " +
          interaction.type +
          " to meeting: " +
          meetingId +
          " at index " +
          index +
          ": " +
          JSON.stringify(interaction)
      )

      Vue.set(state.interactions, interaction.interactionId, interaction)
      let meetingInteractionIds = state.meetingInteractions[meetingId]
      meetingInteractionIds.splice(index, 0, interaction.interactionId)
      Vue.set(state.meetingInteractions, meetingId, meetingInteractionIds)
    },

    updateInteraction(state, { interactionId, config }) {
      let interaction = state.interactions[interactionId]
      if (!interaction) {
        log.warn("Error - interaction with ID " + interactionId + " not found")
        return
      }

      log.info("Updating interaction " + interactionId + " (a " + interaction.type + ")")

      // We replace the whole config to avoid setting the individual type-specific properties
      Vue.set(interaction, "config", config)
    },

    /**
     * update interaction list of specified meeting in state
     *
     * @param state
     * @param meetingId
     * @param interactions
     * @param activeInteractionId
     */
    updateInteractions(state, { meetingId, interactions }) {
      // collect interactionIds and update interactions in state
      let newInteractionIds = []
      interactions.forEach((interaction) => {
        Vue.set(state.interactions, interaction.interactionId, interaction)
        newInteractionIds.push(interaction.interactionId)
      })

      // in case some interactions have been deleted in provided list, delete them from state
      let oldInteractionIds = state.meetingInteractions[meetingId]
      if (Array.isArray(oldInteractionIds)) {
        let deletedInteractionIds = oldInteractionIds.filter((id) => !newInteractionIds.includes(id))
        deletedInteractionIds.forEach((id) => delete state.interactions[id])
      }

      // update interaction list for meeting in state
      Vue.set(state.meetingInteractions, meetingId, newInteractionIds)
    },

    /**
     * Called after dragging an interaction. Notifying the server is done by the moveInteraction action below.
     */
    updateInteractionOrder(state, { meetingId, interactions }) {
      if (!Array.isArray(interactions)) {
        log.warn("Error - passed non-array to updateInteractions mutation.")
      }
      let interactionIds = interactions.map((interaction) => interaction.interactionId)
      Vue.set(state.meetingInteractions, meetingId, interactionIds)
    },

    deleteInteraction(state, { meetingId, interactionId }) {
      let meeting = getMeetingById(state, meetingId)
      if (!meeting) {
        log.warn("Error - meeting with ID " + meetingId + " not found")
        return
      }

      let meetingInteractions = state.meetingInteractions[meetingId]
      let index = Array.isArray(meetingInteractions) ? meetingInteractions.indexOf(interactionId) : -1
      if (index < 0) {
        log.debug("Interaction with ID", interactionId, "probably already deleted")
        return
      }

      log.info("Deleting interaction " + interactionId + " of meeting " + meetingId + " at index " + index)
      meetingInteractions.splice(index, 1)
      Vue.set(state.meetingInteractions, meetingId, meetingInteractions)
      delete state.interactions[interactionId]
    },

    moveInteraction(state, { meetingId, interactionId, toIndex }) {
      let meeting = getMeetingById(state, meetingId)
      if (!meeting) {
        log.warn("Error - meeting with ID " + meetingId + " not found")
        return
      }

      let meetingInteractions = state.meetingInteractions[meetingId]
      let index = Array.isArray(meetingInteractions) ? meetingInteractions.indexOf(interactionId) : -1
      if (index < 0) {
        log.trace("Interaction with ID", interactionId, "probably already deleted")
        return
      }

      if (index !== toIndex) {
        log.debug("Going to update index of interaction", interactionId, "from", index, "to", toIndex)
        // Stolen from https://stackoverflow.com/a/6470794/5515410
        meetingInteractions.splice(index, 1)
        meetingInteractions.splice(toIndex, 0, interactionId)
        Vue.set(state.meetingInteractions, meetingId, meetingInteractions)
      } else {
        log.trace("Interaction already at correct index: ", toIndex)
      }
    },

    // --------------------------------- Navigation mutations ---------------------------------

    navigate(state, { meetingId, index, phaseName }) {
      let meeting = getMeetingById(state, meetingId)
      if (!meeting) {
        log.warn("Error - meeting with ID " + meetingId + " not found")
        return
      }

      let meetingInteractions = state.meetingInteractions[meetingId]
      let interactionId = meetingInteractions[index]
      let interaction = state.interactions[interactionId]

      if (!interaction) {
        log.warn("Error - No interaction at index ", index, " of meeting ", meetingId)
        return
      }

      log.debug("Navigating to", interactionId, "at index", index, "/", phaseName)
      Vue.set(meeting, "activeInteractionId", interactionId)
      Vue.set(interaction.config, "activePhase", phaseName)
    },

    // --------------------------------- Streaming Server mutations ---------------------------------

    setStreamingInfo(state, { meetingId, data }) {
      Vue.set(state.streamingServers, meetingId, data)
      log.info("Streaming server data loaded for meeting", meetingId, ":", JSON.stringify(data))
    },

    deleteStreamingServerEndpoint(state, { meetingId }) {
      Vue.delete(state.streamingServers, meetingId)
    },

    // --------------------------------- Collaboration mutations ---------------------------------

    loadCollaborators(state, data) {
      state.meetingCollaborators = data
    },

    addCollaborator(state, { meetingId, userRoles }) {
      if (state.meetingCollaborators[meetingId] === undefined) {
        Vue.set(state.meetingCollaborators, meetingId, [])
      }

      if (userRoles.length > 0) state.meetingCollaborators[meetingId] = userRoles
      else Vue.delete(state.meetingCollaborators, meetingId)
    },

    deleteCollaborator(state, { meetingId, userId }) {
      const index = state.meetingCollaborators[meetingId].indexOf(userId)
      if (index > -1) state.meetingCollaborators[meetingId].splice(index, 1)
    },
  },

  actions: {
    /**
     * Init user's custom sort and filter config. Returns a promise that resolves when init is complete.
     *
     * @param state
     * @param commit
     * @param rootGetters
     * @returns {null}
     */
    initSortAndFilter({ state, commit, rootGetters }) {
      console.log("----- initSortAndFilter")
      // only init once
      if (!state.sortAndFilterInitPromise) {
        let sortAndFilterInitPromise = rootGetters["user/userSettingsPromise"].then((userSettings) => {
          console.log("----- initSortAndFilter.userSettingsPromise resolved")
          // Reset the meeting filters to their default (depending on the selectedTenant to default or adminDefault)
          commit("resetMeetingFilterCriteria")

          // commit user's custom sort config to state, if available
          let activeSortOption = userSettings?.properties?.[defaultUserProperties.ACTIVE_MEETING_SORT_OPTION].value
          if (!isNaN(activeSortOption)) commit("setLastActiveSort", Number(activeSortOption))

          // commit user's custom filter config to state, if available
          let filterCriteriaJson = userSettings?.properties?.[defaultUserProperties.MEETING_FILTER_CONFIG]?.value
          if (filterCriteriaJson) {
            let filterCriteria = JSON.parse(filterCriteriaJson)
            if (filterCriteria && typeof filterCriteria == "object") {
              commit("setMeetingFilterCriteria", filterCriteria)
            }
          }

          // init meetingRequestHandler with configured sort and filter config
          let filter = requestFilter(
            state.meetingFilterCriteria,
            state.sortOptions.options[state.sortOptions.active],
            rootGetters
          )
          console.log("filter", filter)
          meetingRequestHandler.setFilter(filter)
        })
        commit("sortAndFilterInitPromise", sortAndFilterInitPromise)
      }

      return state.sortAndFilterInitPromise
    },

    /**
     * Loading the next page of the meeting list.
     * (Respecting sort, filter, search and pagination.)
     * If purge is true, the list will be cleared and the first page is loaded again.
     *
     * @param state
     * @param dispatch
     * @param commit
     * @param purge
     */
    async loadMeetings({ state, dispatch, commit }, { purge }) {
      if (meetingRequestHandler.LoadStatus === LOAD_STATES.LOADING) return
      commit("loadStart")

      try {
        await dispatch("getAutojoin")
      } catch (e) {
        // Valid case as we may not be allowed to do anything autojoin-related
        log.warn(e)
      }

      await dispatch("initSortAndFilter")

      if (purge) meetingRequestHandler.setCurrentPage(0)

      const autojoinFilterActive = state.meetingFilterCriteria
        .find((filter) => filter.type === "autojoinFilter")
        .options.find((option) => option.text === "autojoinMeeting").selected

      let resultPromise
      if (autojoinFilterActive && typeof state.autojoinFilter !== "undefined") {
        resultPromise = dispatch("loadMeeting", state.autojoinFilter).then((resp) => {
          if (typeof resp === "object") {
            return [resp]
          } else {
            return []
          }
        })
      } else if (autojoinFilterActive) {
        resultPromise = Promise.resolve([])
      } else {
        resultPromise = meetingRequestHandler.retrieveNextPage()
      }

      // Pending: When we replace Vue-Resource by Axios or Fetch, we could also *abort* running searches
      resultPromise
        .then((result) => {
          commit("loadMeetings", {
            meetings: result,
            purge: purge,
          })
          dispatch("loadCollaborators")
          dispatch("tenants/loadAssignedTenants", null, { root: true }) // for displaying tenant names
        })
        .catch((error) => log.warn(error))
    },

    /**
     * Loading all meetings on the meeting list from page 0 to the specified page.
     * (Respecting sort, filter, search and pagination.)
     *
     * @param state
     * @param dispatch
     * @param commit
     * @param rootGetters
     * @param page
     */
    async loadMeetingsToPage({ state, dispatch, commit }, page = null) {
      if (meetingRequestHandler.LoadStatus === LOAD_STATES.LOADING) return
      commit("loadStart")

      try {
        await dispatch("getAutojoin")
      } catch (e) {
        // Valid case as we may not be allowed to do anything autojoin-related
        log.warn(e)
      }

      await dispatch("initSortAndFilter")

      if (page !== null) {
        meetingRequestHandler.setCurrentPage(page)
      }

      const autojoinFilterActive = state.meetingFilterCriteria
        .find((filter) => filter.type === "autojoinFilter")
        .options.find((option) => option.text === "autojoinMeeting").selected

      let resultPromise
      if (autojoinFilterActive && typeof state.autojoinFilter !== "undefined") {
        resultPromise = dispatch("loadMeeting", state.autojoinFilter).then((resp) => {
          if (typeof resp === "object") {
            return [resp]
          } else {
            return []
          }
        })
      } else if (autojoinFilterActive) {
        resultPromise = Promise.resolve([])
      } else {
        resultPromise = meetingRequestHandler.retrieveToCurrentPage()
      }

      resultPromise
        .then((result) => {
          commit("loadMeetings", {
            meetings: result,
            purge: true, // Purge old meeting set
          })
          dispatch("loadCollaborators")
          dispatch("tenants/loadAssignedTenants", null, { root: true }) // for displaying tenant names
        })
        .catch((error) => log.warn(error))
    },

    /**
     * Loading an individual meeting for the cockpit area.
     *
     * (This includes loading additional data such as the meeting's auth settings).
     *
     * @param commit
     * @param getters
     * @param dispatch
     * @param id
     */
    loadMeeting({ commit, getters, dispatch }, id) {
      return new Promise((resolve, reject) => {
        if (getters["byId"](id)) {
          resolve()
          return
        }

        Vue.http.get(api.MEETINGS(id)).then(
          (response) => {
            // Trigger loading of main meeting if breakout meeting was loaded and its main meeting doesn't exist yet
            if (response.body?.parentId && !getters["byId"](response.body.parentId)) {
              log.info("breakout meeting loaded with unknown parent - loading parent meeting now")
              dispatch("loadMeeting", response.body.parentId)
              // We don't need to load the waiting room here on demand as it is loaded by CockpitLayout
            }
            commit("loadMeeting", {
              meeting: response.body,
            })
            dispatch("loadCollaborators")
            dispatch("tenants/loadAssignedTenants", null, { root: true })
            resolve(response.data)
          },
          (response) => {
            log.error(
              "Error loading meeting ",
              id,
              " - error: ",
              response.status,
              "/ error message:",
              response.statusText
            )
            reject(response)
          }
        )
      })
    },

    loadMeetingCounts({ commit }, tenantId) {
      return new Promise((resolve, reject) => {
        Vue.http.get(api.TENANT_MEETING_COUNTS(tenantId)).then(
          (response) => {
            commit("setMeetingCounts", {
              tenantId,
              data: response.body,
            })
          },
          (response) => {
            log.error(
              "Failed to load open meeting counts - error:",
              response.status,
              " / error message:",
              response.statusText
            )
            reject(response)
          }
        )
      })
    },

    /**
     * Subscribes us to publications about the number of (open) meetings per type and total.
     *
     * @param dispatch
     * @param commit
     * @param tenantId The ID of the tenant whose meetings will be counted.
     */
    subscribeForMeetingCounts({ dispatch, commit }, { tenantId }) {
      log.debug("Subscribe for total meeting count and open counts of tenant", tenantId)
      dispatch(
        "publications/subscribeToPublication",
        {
          pubType: "MeetingCounts",
          newChannelConfig: {
            tenantId,
          },
          callback: (publication) => {
            if (!publication || !publication.channel || !publication.data) {
              return // nothing published
            }

            let tenantId = publication.channel.tenantId
            if (!tenantId) {
              log.error("Cannot process publication count message without tenantId")
            } else {
              commit("setMeetingCounts", {
                tenantId,
                data: publication.data,
              })
            }
          },
        },
        { root: true }
      )
    },

    /**
     * create new meeting on server
     *
     * @param commit
     * @param dispatch
     * @param data
     * @returns {Promise<any>}
     */
    createMeeting({ commit, dispatch }, { data }) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.MEETINGS(), data).then(
          (response) => {
            commit("createMeeting", {
              data: response.data,
              id: response.data.id,
            })
            dispatch("loadCollaborators")
            dispatch("tenants/loadAssignedTenants", null, { root: true }) // for displaying tenant names
            resolve(response.data)
          },
          (response) => {
            log.error("Could not create meeting, error: ", response.status, "/ error message:", response.statusText)
            reject(response)
          }
        )
      })
    },

    hideMeeting({ commit, dispatch }, id) {
      return new Promise((resolve, reject) => {
        Vue.http.delete(api.MEETINGS(id)).then(
          (response) => {
            commit("hideMeeting", { id: id })
            // Reload the paging information
            dispatch("loadMeetingsToPage")
            resolve(response.data)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },

    /**
     * Starts a meeting. The meeting ID as well as auto-stop settings of the meeting are passed via the argument object
     * (auto stop the meeting after a given interval after the last participant has left the meeting (or any breakouts)
     * or auto stop at a specified point in time).
     *
     * @param commit
     * @param data
     * @return {Promise<unknown>}
     */
    startMeeting({ commit }, data) {
      let url = api.START_MEETING(data.id)
      let params = {}
      if (data?.autoCloseAfterLastLifeSignMinutes) {
        params.autoCloseAfterLastLifeSignMinutes = data.autoCloseAfterLastLifeSignMinutes
      }
      if (data?.autoCloseAtTime) {
        params.autoCloseAtTime = data.autoCloseAtTime
      }
      let config = {}
      config.params = params

      log.info("Going to start the meeting", JSON.stringify(params))

      const self = this
      return new Promise((resolve, reject) => {
        Vue.http.post(url, null, config).then(
          (response) => {
            let waitingRoomSupported = self.getters["meetings/getMainMeetingById"](data.id)?.waitingRoomSupport
            if (waitingRoomSupported) {
              commit("startWaitingRoom", data)
            }
            commit("startMeeting", data)
            resolve(response)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },

    /**
     * Stops the given meeting independent of any connected clients and the configured auto-stop settings.
     *
     * @param commit
     * @param id
     * @return {Promise<unknown>}
     */
    stopMeeting({ commit }, { id }) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.STOP_MEETING(id)).then(
          (response) => {
            commit("closeWaitingRoom", { id })
            commit("closeMeeting", { id })
            resolve(response)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },

    loadWaitingRoom({ commit }, id) {
      return new Promise((resolve, reject) => {
        Vue.http.get(api.WAITING_ROOM(id)).then(
          (response) => {
            commit("setWaitingRoom", { mainMeetingId: response.data.parentId, waitingRoom: response.data })
            resolve(response.data)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },

    setWaitingRoomText({ commit }, { id, text }) {
      return new Promise((resolve, reject) => {
        Vue.http.put(api.WAITING_ROOM(id), text).then(
          (response) => {
            commit("setWaitingRoom", { mainMeetingId: response.data.parentId, waitingRoom: response.data })
            resolve(response.data)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },

    /**
     * update existing meeting on server
     *
     * @param commit
     * @param data
     * @param id
     * @returns {Promise<any>}
     */
    updateMeeting({ commit }, { data, id }) {
      return new Promise((resolve, reject) => {
        Vue.http.put(api.MEETINGS(id), data).then(
          (response) => {
            commit("updateMeeting", { data: response.data })
            resolve(response.data)
          },
          (response) => {
            log.error("Could not update meeting, error: ", response)
            reject(response)
          }
        )
      })
    },

    /**
     * update meeting owner on server
     *
     * @param commit
     * @param data
     * @param id
     * @returns {Promise<any>}
     */
    updateMeetingOwner({ commit }, { data, id }) {
      return new Promise((resolve, reject) => {
        Vue.http.put(api.MEETING_OWNER(id), data).then(
          (response) => {
            commit("updateProperty", { id: id, key: "ownerId", value: response.data.ownerId })
            resolve(response.data)
          },
          (response) => {
            log.error("Could not update meeting owner, error: ", response)
            reject(response)
          }
        )
      })
    },

    uploadMeetings({ commit }, { formData, parentId, tenantId }) {
      log.debug("Going to upload meeting file")

      let config = { params: {} }

      if (parentId) {
        config.params["parentId"] = parentId
      }
      if (tenantId) {
        config.params["tenantId"] = tenantId
      }

      return new Promise((resolve, reject) => {
        Vue.http.post(api.MEETINGS_UPLOAD(), formData, config).then(
          (response) => {
            commit("createMeetings", { data: response.body })
            resolve(response)
          },
          (response) => {
            log.error("Could not upload meeting, error: ", response)
            reject(response)
          }
        )
      })
    },

    /**
     * set the browsingEnabled flag on a meeting
     *
     * @param commit
     * @param meetingId
     * @param browsingEnabled
     */
    setBrowsingEnabled({ commit }, { meetingId, browsingEnabled }) {
      log.debug("Setting the browsingEnabled flag to ", browsingEnabled)
      commit("setBrowsingEnabled", { meetingId, browsingEnabled })
      communication.sendMessage({
        messageType: "ClientSettingsMessage",
        meetingId: meetingId,
        browsingEnabled: browsingEnabled,
      })
    },

    /**
     * Updates the list of interactions (order has changed)
     *
     * @param commit
     * @param meetingId
     * @param id
     * @param interactions
     */
    updateInteractions({ commit }, { meetingId, interactions }) {
      log.debug("Updating order of interactions of meeting", meetingId)
      commit("updateInteractions", { meetingId, interactions })
    },

    updateProperty({ commit, getters }, { id, key, value }) {
      let meeting = getters["byId"](id)
      if (meeting[key] === value) {
        log.debug("meeting property", key, "didn't change:", value)
        return
      }
      commit("updateProperty", { id, key, value })

      communication.sendMessage({
        messageType: "MeetingUpdateMessage",
        meetingId: id,
        key: key,
        value: value,
      })
    },

    updateProperties({ dispatch }, { id, properties }) {
      for (let property in properties) {
        const key = property
        const value = properties[property]
        dispatch("updateProperty", { id, key, value })
      }
    },

    setMeetingProperty({ commit }, { id, key, value }) {
      commit("setMeetingProperty", { id, key, value })

      let props = {}
      props[key] = value

      communication.sendMessage({
        messageType: "MeetingPropertiesMessage",
        meetingId: id,
        properties: props,
      })
    },

    deleteMeetingProperty({ state }, { meetingId, key }) {
      if (state.meetingProperties[meetingId].hasOwnProperty(key)) {
        return new Promise((resolve, reject) => {
          Vue.http.delete(api.MEETING_PROPERTY_DELETE(meetingId, key)).then(
            (response) => {
              //We don't need to commit the change, because it will be submitted from server to client anyway.
              resolve(response)
            },
            (response) => {
              log.error("Could not delete meeting-property, error: ", response)
              reject(response)
            }
          )
        })
      }

      return new Promise((resolve, reject) => {
        reject("Propertyname wasn't found in meeting properties store.")
      })
    },

    // eslint-disable-next-line no-empty-pattern
    patchMeetingProperties({}, { meetingId, jsonMergePatch }) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(api.MEETING_PROPERTIES_PATCH(meetingId), jsonMergePatch).then(
          (response) => {
            //We don't need to commit the change, because it will be submitted from server to client anyway.
            resolve(response)
          },
          (response) => {
            log.error("Could not patch meeting properties, error: ", response)
            reject(response)
          }
        )
      })
    },

    /**
     * clones an existing meeting by commiting a temporary copy of the target meeting,
     * requesting a clone operation from server, and committing the response as a new
     * meeting.
     *
     * @param commit
     * @param id
     * @returns {Promise<any>}
     */
    cloneMeeting({ commit }, { id }) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.CLONE_MEETING(id), {}).then(
          (response) => {
            commit("createMeetings", { data: response.body })
            resolve(response.data)
          },
          (response) => {
            log.error("Could not clone meeting, error: ", response)
            reject(response)
          }
        )
      })
    },

    /**
     * update existing meeting on server
     *
     * @param commit
     * @param data
     * @param id
     * @returns {Promise<any>}
     */
    joinMeeting({ commit }, { data, id }) {
      return new Promise((resolve, reject) => {
        // noinspection JSUnresolvedFunction
        Vue.http.get(api.JOIN_MEETING(id), data).then(
          (response) => {
            commit("joinMeeting", { data: response.data, id: id })
            resolve(response.data)
          },
          (response) => {
            log.error("Could not join meeting, error: ", response)
            reject(response)
          }
        )
      })
    },

    /**
     * get current autojoin meeting from server
     *
     * @param commit
     * @param rootGetters
     * @returns {Promise<any>}
     */
    getAutojoin({ commit, rootGetters }) {
      return new Promise((resolve, reject) => {
        rootGetters["user/userSettingsPromise"].then((userSettings) => {
          // Tenant users shall not see autojoin indications
          if (userSettings.tenantId) {
            log.debug("Avoid loading autojoin meeting ID for tenant user")
            resolve()
          } else if (!rootGetters["auth/hasPermission"](PERMISSIONS.VIEW_AUTOJOIN_MEETING)) {
            log.debug("Avoid loading autojoin meeting ID for user who has no permission to see it")
            resolve()
          } else {
            Vue.http.get(api.AUTO_JOIN_MEETING()).then(
              (response) => {
                if (typeof response.body !== "undefined") {
                  commit("setAutojoin", response.body.autojoinMeetingId)
                  commit("setAutojoinFilter", response.body.mainMeetingId)
                } else {
                  commit("setAutojoin", null)
                  commit("setAutojoinFilter", null)
                }
                resolve(response.body)
              },
              (response) => {
                commit("loadError")
                reject(response)
              }
            )
          }
        })
      })
    },

    /**
     * set existing meeting as autojoin meeting on server
     *
     * @param commit
     * @param id
     * @returns {Promise<any>}
     */
    setAutojoin({ commit }, id) {
      return new Promise((resolve, reject) => {
        Vue.http.put(api.AUTO_JOIN_MEETING(), id).then(
          (response) => {
            commit("setAutojoin", response.body.autojoinMeetingId)
            commit("setAutojoinFilter", response.body.mainMeetingId)
            resolve(response.data)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },

    /**
     * removes the meeting as autojoin meeting on server
     *
     * @param commit
     * @returns {Promise<any>}
     */
    removeAutojoin({ commit }) {
      return new Promise((resolve, reject) => {
        Vue.http.delete(api.AUTO_JOIN_MEETING()).then(
          (response) => {
            commit("setAutojoin", null)
            commit("setAutojoinFilter", null)
            resolve(response.data)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },

    loadAuthSettings({ state, commit }, { id }) {
      id = getMainMeetingId(state, id)
      if (!id || state.authSettings[id] === "loading")
        // avoid triggering parallel or null requests
        return
      commit("updateAuthSettings", { data: "loading", id: id })
      return new Promise((resolve, reject) => {
        Vue.http.get(api.MEETING_AUTH_SETTINGS(id)).then(
          (response) => {
            commit("updateAuthSettings", { data: response.data, id: id })
            resolve(response.data)
          },
          (response) => {
            commit("updateAuthSettings", { data: null, id: id })
            reject(response)
          }
        )
      })
    },

    updateAuthSettings({ state, commit }, { id, data }) {
      id = getMainMeetingId(state, id)
      return new Promise((resolve, reject) => {
        Vue.http.put(api.MEETING_AUTH_SETTINGS(id), data).then(
          (response) => {
            commit("updateAuthSettings", { data: response.data, id: id })
            resolve(response.data)
          },
          (response) => {
            reject(response)
          }
        )
      })
    },

    persistActiveMeetingSortOption({ state, dispatch }) {
      let activeMeetingSortOption = state.sortOptions.active
      dispatch(
        "user/update",
        { [defaultUserProperties.ACTIVE_MEETING_SORT_OPTION]: { value: activeMeetingSortOption } },
        { root: true }
      )
    },

    persistMeetingFilterConfig({ state, dispatch }) {
      // persist active filters in the format { "timeFilters": ["futureMeetings", "pastMeetings"], "xyFilters": ... }
      let meetingFilterConfig = state.meetingFilterCriteria.reduce((obj, filter) => {
        obj[filter.type] = filter.options.filter((option) => option.selected).map((option) => option.text)
        return obj
      }, {})
      dispatch(
        "user/update",
        { [defaultUserProperties.MEETING_FILTER_CONFIG]: { value: JSON.stringify(meetingFilterConfig) } },
        { root: true }
      )
    },

    // --------------------------------- Interaction actions ---------------------------------

    insertInteraction({ state, commit }, { meetingId, index, interaction }) {
      // Calculate insertion index
      // todo: Do we have to care about incoming insertions while calculating the index?
      if (index < 0) {
        index = state.meetingInteractions[meetingId].length
      }
      log.info("Inserting new tool at index ", index, ": ", JSON.stringify(interaction))
      commit("insertInteraction", { meetingId, index, interaction })
      communication.sendMessage({
        messageType: "InsertInteractionMessage",
        type: interaction.type,
        index: index,
        interactionId: interaction.interactionId,
        config: interaction.config,
      })
    },

    updateInteraction({ commit }, { type, interactionId, config }) {
      log.info("Updating interaction", interactionId, ": ", JSON.stringify(config))
      commit("updateInteraction", { interactionId, config })

      communication.sendMessage({
        messageType: "UpdateInteractionMessage",
        type: type,
        interactionId: interactionId,
        config: config,
      })
    },

    verifyElectionCommission({ commit }, { type, interactionId, config }) {
      log.info("Verifying election commission", interactionId, ": ", JSON.stringify(config))
      commit("updateInteraction", { interactionId, config })

      let correctAnswers = {}
      for (const option of config.options) {
        correctAnswers[option.id] = option.correctAnswer
      }

      communication.sendMessage({
        messageType: "ElectionCommissionVerificationMessage",
        type: type,
        interactionId: interactionId,
        correctAnswers: correctAnswers,
        resultVerified: config.resultVerified,
      })
    },

    cloneInteraction({ commit }, { meetingId, interaction, defaults }) {
      log.info("Cloning interaction with id ", interaction.interactionId, " and type ", interaction.type)

      let index = this.getters["meetings/interactionIndex"](meetingId, interaction.interactionId)
      if (index < 0) {
        log.warn("Error - interaction with ID " + interaction.interactionId + " not found in meeting " + meetingId)
        return
      }

      // Create placeholder interaction with new ID
      let clonedInteraction = {}
      clonedInteraction.interactionId = UUIDv4()
      clonedInteraction.type = interaction.type

      // Use the provided default set of config values,
      // if complete, this will limit references to undefined
      // attributes prior to the true clone arriving
      clonedInteraction.config = defaults

      // Copy over some top level config elements to "look" like the cloned config
      clonedInteraction.config.topic = interaction.config.topic
      clonedInteraction.config.hidden = interaction.config.hidden
      clonedInteraction.config.activePhase = interaction.config.activePhase

      clonedInteraction.isPlaceholder = true
      let clonedIndex = index + 1

      // Insert placeholder
      log.info(
        "Inserting temporary placeholder interaction ",
        clonedInteraction.interactionId,
        " at index ",
        clonedIndex
      )
      commit("insertInteraction", {
        meetingId: meetingId,
        index: clonedIndex,
        interaction: clonedInteraction,
      })

      // Send clone instruction to server
      log.info("Sending CloneInteractionMessage to server")
      communication.sendMessage({
        messageType: "CloneInteractionMessage",
        type: interaction.type,
        interactionId: interaction.interactionId,
        newInteractionId: clonedInteraction.interactionId,
      })
    },

    deleteInteraction({ commit }, { meetingId, interaction }) {
      log.info("Going to delete interaction: ", interaction.interactionId)
      commit("deleteInteraction", {
        meetingId: meetingId,
        interactionId: interaction.interactionId,
      })

      communication.sendMessage({
        messageType: "RemoveInteractionMessage",
        type: interaction.type,
        interactionId: interaction.interactionId,
      })
    },

    // eslint-disable-next-line no-unused-vars
    moveInteraction({ commit }, { meetingId, interactionId, type, to }) {
      log.info("Going to move interaction", interactionId, "to index", to)

      // No mutation required here as the list is updated already via updateInteractionOrder called from CockpitScript

      communication.sendMessage({
        messageType: "MoveInteractionMessage",
        meetingId: meetingId,
        interactionId: interactionId,
        type: type,
        toIndex: to,
      })
    },

    // --------------------------------- Navigation actions ---------------------------------

    navigate({ commit }, { meetingId, interactionId, phaseName }) {
      log.info("Navigating meeting", meetingId, ": interaction", interactionId, "phase", phaseName)

      let index = this.getters["meetings/interactionIndex"](meetingId, interactionId)

      commit("navigate", { meetingId, index, phaseName })

      communication.sendMessage({
        messageType: "NavigateToIndexMessage",
        toolIndex: index,
        phaseName: phaseName.toLocaleUpperCase(),
      })
    },

    /**
     * Set last active sort to given option and trigger handler update + meeting reload
     *
     * @param state
     * @param commit
     * @param dispatch
     * @param rootGetters
     * @param sortVal the idx of the sort option to use
     */
    setLastActiveSort({ state, commit, dispatch, rootGetters }, sortVal) {
      commit("setLastActiveSort", sortVal)
      meetingRequestHandler.setFilter(
        requestFilter(state.meetingFilterCriteria, state.sortOptions.options[state.sortOptions.active], rootGetters)
      )
      dispatch("loadMeetings", { purge: true })
    },

    /**
     * Update meeting filter criteria (toggle one filter option) and trigger handler update + meeting reload
     *
     * @param state
     * @param commit
     * @param dispatch
     * @param rootGetters
     * @param filterIdx The filter group to update
     * @param optionIdx The option to toggle
     */
    updateMeetingFilterCriteria({ state, commit, dispatch, rootGetters }, { filterIdx, optionIdx }) {
      console.log("----- action: updateMeetingFilterCriteria")
      commit("updateMeetingFilterCriteria", { filterIdx, optionIdx })
      meetingRequestHandler.setFilter(
        requestFilter(state.meetingFilterCriteria, state.sortOptions.options[state.sortOptions.active], rootGetters)
      )
      dispatch("loadMeetings", { purge: true })
    },

    /**
     * Reset meeting filter criteria to default values and trigger handler update + meeting reload
     *
     * @param state
     * @param commit
     * @param dispatch
     * @param rootGetters
     */
    resetMeetingFilterCriteria({ state, commit, dispatch, rootGetters }) {
      console.log("----- action: resetMeetingFilterCriteria")
      commit("resetMeetingFilterCriteria")
      meetingRequestHandler.setFilter(
        requestFilter(state.meetingFilterCriteria, state.sortOptions.options[state.sortOptions.active], rootGetters)
      )
      dispatch("loadMeetings", { purge: true })
    },

    /**
     * (Re)load meetings (dispatched by tenants.setTenantFilterId after the tenantFilterId changed)
     *
     * @param state
     * @param dispatch
     * @param rootGetters
     */
    updateHandlerAndLoadMeetings({ state, dispatch, rootGetters }) {
      log.info("Reloading meetings")

      meetingRequestHandler.setFilter(
        requestFilter(state.meetingFilterCriteria, state.sortOptions.options[state.sortOptions.active], rootGetters)
      )
      dispatch("loadMeetings", { purge: true })
    },

    /**
     * Update search query and trigger handler update + meeting reload
     *
     * @param state
     * @param commit
     * @param dispatch
     * @param search search query to use
     */
    setSearchQuery({ state, commit, dispatch }, search) {
      commit("setSearchQuery", search)
      meetingRequestHandler.setSearchString(state.searchQuery)
      dispatch("loadMeetings", { purge: true })
    },

    startWaitingRoom({ commit }, { meetingId }) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.WAITING_ROOM_ACTIVATIONS(meetingId), null).then(
          (success) => {
            commit("meetings/startWaitingRoom", { id: meetingId }, { root: true })
            // Nothing else to do here
            resolve(success)
          },
          (error) => {
            // Log error
            log.error(`${error.status} : ${JSON.stringify(error.body)}`)

            reject(error)
          }
        )
      })
    },

    closeWaitingRoom({ commit }, { meetingId }) {
      return new Promise((resolve, reject) => {
        Vue.http.post(api.WAITING_ROOM_DEACTIVATIONS(meetingId), null).then(
          (success) => {
            commit("meetings/closeWaitingRoom", { id: meetingId }, { root: true })
            // Nothing else to do here
            resolve(success)
          },
          (error) => {
            // Log error
            log.error(`${error.status} : ${JSON.stringify(error.body)}`)

            reject(error)
          }
        )
      })
    },

    // --------------------------------- Streaming Server actions ---------------------------------

    loadStreamingEndpoint({ commit }, { meetingId }) {
      Vue.http.get(api.MEETING_STREAMING_SERVER(meetingId)).then(
        (response) => commit("setStreamingInfo", { meetingId, data: response.body }),
        () => log.error("Error loading streaming endpoint of meeting", meetingId)
      )
    },

    createStreamingServerEndpoint({ commit }, { meetingId }) {
      log.info("Setting streaming server endpoint of meeting", meetingId)

      Vue.http.put(api.MEETING_STREAMING_SERVER(meetingId)).then(
        (response) => commit("setStreamingInfo", { meetingId, data: response.body }),
        () => log.error("Error creating streaming endpoint of meeting", meetingId)
      )
    },

    deleteStreamingServerEndpoint({ commit }, { meetingId }) {
      log.info("Deleting streaming server endpoint of meeting", meetingId)
      // let endpointData = state.streamingServers[meetingId]

      Vue.http.delete(api.MEETING_STREAMING_SERVER(meetingId)).then(
        () => commit("deleteStreamingServerEndpoint", { meetingId }),
        () => log.error("Error deleting streaming endpoint of meeting", meetingId)
      )
    },

    // --------------------------------- Collaboration actions ---------------------------------

    /**
     * Load collaborators for locally held set of meetings
     *
     * @param commit
     * @param state
     * @param dispatch
     * @param rootGetters
     */
    loadCollaborators({ commit, state, dispatch, rootGetters }) {
      const requestPromises = []

      for (const meetingId in state.meetings) {
        // Only request user roles if logged in user has needed meeting permission
        if (!rootGetters["auth/hasMeetingPermission"](meetingId, PERMISSIONS.VIEW_USER_ROLE_MEETING)) continue

        const requestPromise = Vue.http.get(api.MEETING_COLLABORATOR_ROLES(meetingId)).then(
          (response) => {
            commit("addCollaborator", { meetingId, userRoles: response.body })
          },
          () => {
            log.error("Error loading collaborators.")
          }
        )

        requestPromises.push(requestPromise)
      }

      Promise.allSettled(requestPromises).then(() => {
        dispatch("users/loadPublicUserSet", null, { root: true }) // for displaying collaborator usernames
      })
    },

    /**
     * Load collaborators for a specific meeting
     *
     * @param commit
     * @param rootGetters
     * @param meetingId
     */
    loadMeetingCollaborators({ commit, rootGetters }, meetingId) {
      return new Promise((resolve, reject) => {
        // Only request user roles if logged in user has needed meeting permission
        if (!rootGetters["auth/hasMeetingPermission"](meetingId, PERMISSIONS.VIEW_USER_ROLE_MEETING)) reject()

        Vue.http.get(api.MEETING_COLLABORATOR_ROLES(meetingId)).then(
          (response) => {
            commit("addCollaborator", { meetingId, userRoles: response.body })
            resolve()
          },
          () => {
            log.error("Error loading collaborators.")
            reject()
          }
        )
      })
    },

    /**
     * Update (add/remove) collaborators of a specific meeting
     *
     * @param dispatch
     * @param meetingId
     * @param collaborators
     * @returns {Promise<any>}
     */
    updateCollaborators({ dispatch }, { meetingId, collaborators }) {
      return new Promise((resolve, reject) => {
        Vue.http.put(api.MEETING_COLLABORATOR_ROLES(meetingId), collaborators).then(
          (response) => {
            dispatch("loadMeetingCollaborators", meetingId).finally(() => {
              resolve(response.body)
            })
          },
          () => {
            log.error("Error updating collaborators for meeting ", meetingId)
            reject()
          }
        )
      })
    },
  },

  getters: {
    /**
     * Get whether meetings have successfully loaded
     *
     * @returns {boolean}
     */
    hasLoaded: (state) => state.status === "success",

    /**
     * Get whether the store has been mutated between loads
     *
     * @returns {boolean}
     */
    isMeetingListDirty: (state) => state.dirtyMeetingList,

    /**
     * Get paged, sorted and filtered array of main meetings. Used for displaying the meeting list.
     *
     * @returns {any[]}
     */
    list: (state) => state.meetingKeys.map((id) => state.meetings[id]),

    /**
     * Get meeting ids
     *
     * @returns {String[]}
     */
    getIds: (state) => state.meetingKeys,

    /**
     * Get array containing all meetings currently kept in store.
     *
     * (Contrary to the 'list' getter above, this getter also returns meetings that
     * have been accessed via a direct link or have recently been created but are
     * not displayed in the meeting list due to filter criteria or pagination.)
     *
     * @param state
     * @returns {string[]}
     */
    allMeetings: (state) => Object.values(state.meetings),

    /**
     * Get meeting by Id
     *
     * @returns {function(*)}
     */
    byId: (state) => (id) => {
      return getMeetingById(state, id)
    },

    /**
     * Get main meeting of the given meeting ID, if necessary resolving the parent if ID is of a breakout meeting.
     *
     * @param state
     * @return {(function(String): (Object))}
     */
    getMainMeetingById: (state) => (id) => {
      let meeting = state.meetings[id]
      if (meeting) {
        return meeting
      }
      let mainMeetingId = state.breakoutMainMapping[id]
      if (mainMeetingId) {
        return state.meetings[mainMeetingId]
      }
    },

    getWaitingRoomByMeetingId: (state) => (id) => {
      let mainMeetingId = id
      let mainMeeting = state.meetings[id]
      if (!mainMeeting) {
        // It's not a main meeting - lookup main meeting
        mainMeetingId = state.breakoutMainMapping[id]
      }
      let waitingRoomId = state.waitingRoomIds[mainMeetingId]
      return state.waitingRooms[waitingRoomId]
    },

    /**
     * Get whether meeting is set as autojoin meeting
     * @returns {function(*): boolean}
     */
    isAutojoin: (state) => (id) => {
      return id === state.autojoinId
    },

    /**
     * Whether any meeting is set as autojoin
     * @param state
     * @returns {function(*): *}
     */
    hasAutojoinMeeting: (state) => {
      return typeof state.autojoinId !== "undefined" && state.autojoinId !== null
    },

    getMeetingCounts: (state) => (tenantId) => {
      return state.meetingCounts[tenantId]
    },

    /**
     * Gets the number of open meetings of the given tenant and meetingType. If meetingType is null the number of
     * open meetings independent of the type is returned (i.e., the sum of the open counts of each meetingType)
     *
     * @return {function(String, String): Number}
     */
    getOpenMeetingCount: (state) => (meetingType, tenantId) => {
      if (!state.meetingCounts[tenantId]) {
        return null
      }
      if (!meetingType) {
        return state.meetingCounts[tenantId].totalOpenCount
      } else {
        return state.meetingCounts[tenantId].openCounts[meetingType]
      }
    },

    /**
     * Get the value of a boolean meeting property
     * (Checks for the property value to equal true, everything else is considered false)
     *
     * @param state
     * @returns {Function}
     */
    getBooleanMeetingProperty: (state) => (id, key) => {
      let props = state.meetingProperties[id]
      if (props) {
        return state.meetingProperties[id][key] === "true"
      }
      return false
    },

    /**
     * Get the value of a meeting property
     *
     * @param state
     * @returns {Function}
     */
    getMeetingProperty: (state) => (id, key) => {
      let props = state.meetingProperties[id]
      if (props) {
        return state.meetingProperties[id][key]
      }
    },

    /**
     * Get all meeting properties
     * @param state
     * @return {(function(*=): (*))|*}
     */
    getMeetingPropertiesByMeetingId: (state) => (meetingId) => {
      if (state.meetingProperties.hasOwnProperty(meetingId)) {
        return state.meetingProperties[meetingId]
      }

      return {}
    },

    /**
     * Get all meeting collaborators
     *
     * @param state
     * @returns {Function}
     */
    collaboratorsList: (state) => {
      let list = []
      Object.keys(state.meetings).forEach((id) => {
        if (state.meetingCollaborators[id] !== undefined)
          list.push.apply(
            list,
            state.meetingCollaborators[id].map((obj) => obj.userId)
          )
      })
      return list
    },

    /**
     * Get the collaborators of a specific meeting
     *
     * @param state
     * @returns {Function}
     */
    collaboratorsById: (state) => (id) => {
      return state.meetingCollaborators[id] || []
    },

    /**
     * Get the display state of the burger menu for a meeting
     *
     * @param state
     * @returns {function(*): *}
     */
    browsingEnabled: (state) => (id) => {
      return state.browsingEnabled[id]
    },

    authSettings: (state) => (id) => {
      id = getMainMeetingId(state, id)
      let authSettings = state.authSettings[id]
      return typeof authSettings === "object" ? authSettings : null
    },

    hasBreakoutMeetings: (state) => (id) => {
      if (!id) {
        return
      }

      let meeting = state.meetings[id]
      return meeting?.breakoutMeetings?.length > 0
    },

    /**
     * get meeting filter criteria
     * @param state
     * @returns {*}
     */
    meetingFilterCriteria: (state) => state.meetingFilterCriteria,

    /**
     * get whether the currently active filter criteria is more restrictive than the default configuration
     * @param state
     * @returns {boolean}
     */
    isFilterCriteriaRestrictive: (state) =>
      state.meetingFilterCriteria.some((filterGroup) =>
        filterGroup?.options?.some((option) => option?.default === true && option?.selected !== true)
      ),

    /**
     * get sort options
     * @param state
     * @returns {*}
     */
    sortOptions: (state) => state.sortOptions.options,

    /**
     * get last sort setting
     * @param state
     * @returns {*}
     */
    lastActiveSort: (state) => state.sortOptions.active,

    /**
     * get search query
     * @param state
     * @returns {*}
     */
    getSearchQuery: (state) => state.searchQuery,

    /**
     * Returns the array of interactions of the given meeting or an empty array
     * @param state
     * @returns {Function}
     */
    interactions: (state) => (id) => {
      let meetingInteractions = state.meetingInteractions[id]
      if (!Array.isArray(meetingInteractions)) {
        return []
      }
      return meetingInteractions.map((interactionId) => state.interactions[interactionId])
    },

    interaction: (state) => (interactionid) => {
      return state.interactions[interactionid]
    },

    /**
     * Returns the index of the specified interaction in the specified meeting
     * @param state
     * @returns {function(*, *=): number}
     */
    interactionIndex: (state) => (meetingId, interactionId) => {
      let meetingInteractions = state.meetingInteractions[meetingId]
      return Array.isArray(meetingInteractions) ? meetingInteractions.indexOf(interactionId) : -1
    },

    /**
     * Returns a boolean indicating if another page of meetings can be retrieved
     * @returns Boolean
     */
    canRetrieveMoreMeetings: () => meetingRequestHandler.CurrentPage < meetingRequestHandler.TotalPages - 1,

    statistics: (state) => (/*id*/) => {
      return state.statistics
    },

    streamingEndpoint: (state) => (id) => {
      let data = state.streamingServers[id]
      if (data) {
        return data.endpoint
      }
      return ""
    },

    activeInteractionId: (state) => (meetingId) => {
      const meeting = getMeetingById(state, meetingId)
      if (meeting.operationMode === "ASYNC") {
        return "N/A" // Don't use null as that's the default for new interactions
      } else {
        return meeting.activeInteractionId
      }
    },

    isAutojoinFilterActive: (state) => {
      return state.meetingFilterCriteria
        .find((filter) => filter.type === "autojoinFilter")
        .options.find((option) => option.text === "autojoinMeeting").selected
    },
  },
}
